import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

interface PaymentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function PaymentForm({ open, onOpenChange, onSuccess }: PaymentFormProps) {
  const [loading, setLoading] = useState(false);

  const { data: students } = useQuery({
    queryKey: ["students-select"],
    queryFn: async () => {
      const { data, error } = await supabase.from("students").select("id, first_name, last_name, student_number").order("last_name");
      if (error) throw error;
      return data;
    },
  });

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      student_id: "",
      invoice_id: "",
      amount: "",
      payment_method: "",
      reference_number: "",
      payment_date: new Date().toISOString().split("T")[0],
    },
  });

  const selectedStudentId = watch("student_id");

  const { data: invoices } = useQuery({
    queryKey: ["invoices-for-student", selectedStudentId],
    queryFn: async () => {
      const { data, error } = await supabase.from("invoices").select("id, amount, status, description").eq("student_id", selectedStudentId);
      if (error) throw error;
      return data;
    },
    enabled: !!selectedStudentId,
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = { ...data, amount: parseFloat(data.amount) };
      const { error } = await supabase.from("payments").insert(payload);
      if (error) throw error;
      toast({ title: "Payment recorded successfully" });
      reset();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Student *</Label>
            <Select value={watch("student_id")} onValueChange={(v) => { setValue("student_id", v); setValue("invoice_id", ""); }}>
              <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent position="popper" className="z-[9999]">
                {students?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.student_number} — {s.first_name} {s.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Invoice *</Label>
            <Select value={watch("invoice_id")} onValueChange={(v) => setValue("invoice_id", v)} disabled={!selectedStudentId}>
              <SelectTrigger><SelectValue placeholder={selectedStudentId ? "Select invoice" : "Select student first"} /></SelectTrigger>
              <SelectContent>
                {invoices?.map((inv) => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.description || "Invoice"} — ${inv.amount} ({inv.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input id="amount" type="number" step="0.01" {...register("amount", { required: "Required" })} />
              {errors.amount && <p className="text-sm text-destructive">{errors.amount.message as string}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input id="payment_date" type="date" {...register("payment_date")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={watch("payment_method")} onValueChange={(v) => setValue("payment_method", v)}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input id="reference_number" {...register("reference_number")} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading ? "Saving..." : "Record Payment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
